import React, { useState, useEffect, useMemo, useCallback, useLayoutEffect } from "react";
import { View, StyleSheet, TextInput, Pressable, Platform, ScrollView } from "react-native";
import { ThemedText } from "@/components/ThemedText";
import { useTheme } from "@/hooks/useTheme";
import useDateTime from "@/hooks/useDateTime";
import useAuth from "@/hooks/useAuth";
import usePermissions from "@/hooks/usePermissions";
import { useCurrentEntity } from "@/contexts/CurrentEntityContext";
import { useNavigationHistory } from "@/contexts/NavigationHistoryContext";
import { estimateService } from "@/services/estimateService";
import { EstimateType } from "@/types/db/estimates";
import { router } from "expo-router";
import { useNavigation } from "@react-navigation/native";
import { MaterialCommunityIcons } from "@expo/vector-icons";
import { Table, ColumnDef, EditableOption } from "@/components/NewTable";
import { theme } from "@/constants/themes";
import { formatCurrency as formatCurrencyLib } from "@/lib/format/currency";

// Status color mapping
const STATUS_COLORS: Record<string, keyof Pick<theme, "complete" | "pending" | "attention" | "neutral" | "todo">> = {
  DRAFT: "todo",
  SENT: "pending",
  ACCEPTED: "complete",
  REJECTED: "attention",
  EXPIRED: "neutral",
};

export function RelatedEstimatesScreen() {
  const { colors } = useTheme();
  const { formatDate } = useDateTime();
  const { displayNames } = useAuth();
  const { estimates: estimatePermissions } = usePermissions();
  const currentEntity = useCurrentEntity();
  const { pushNavigation } = useNavigationHistory();
  const navigation = useNavigation();
  const [estimates, setEstimates] = useState<EstimateType[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [searchValue, setSearchValue] = useState("");

  // Set up header with search
  useLayoutEffect(() => {
    navigation.setOptions({
      headerTitle: () => (
        <View style={styles.headerTitleContainer}>
          <ThemedText style={styles.headerTitle}>{displayNames?.estimates?.plural || "Estimates"}</ThemedText>
          <View style={[styles.headerSearchContainer, { backgroundColor: colors.backgroundSecondary }]}>
            <MaterialCommunityIcons
              name="magnify"
              size={16}
              color={colors.textSecondary}
              style={styles.headerSearchIcon}
            />
            <TextInput
              value={searchValue}
              onChangeText={setSearchValue}
              placeholder="Search..."
              placeholderTextColor={colors.textSecondary}
              style={[styles.headerSearchInput, { color: colors.text }]}
              autoCapitalize="none"
              autoCorrect={false}
            />
            {searchValue.length > 0 && (
              <Pressable onPress={() => setSearchValue("")} style={styles.headerClearButton}>
                <MaterialCommunityIcons name="close-circle" size={14} color={colors.textSecondary} />
              </Pressable>
            )}
          </View>
        </View>
      ),
    });
  }, [navigation, searchValue, colors, displayNames?.estimates?.plural]);

  useEffect(() => {
    const fetchEstimates = async () => {
      if (!currentEntity?.currentId || !currentEntity?.currentPanel) {
        setLoading(false);
        return;
      }

      setLoading(true);
      setError(null);

      try {
        let fetchedEstimates: EstimateType[] = [];

        switch (currentEntity.currentPanel) {
          case "project":
            fetchedEstimates = await estimateService.getEstimatesByProjectId(currentEntity.currentId);
            break;
          case "client":
            fetchedEstimates = await estimateService.getEstimatesByClientId(currentEntity.currentId);
            break;
          default:
            console.warn(`No estimate relationship defined for entity type: ${currentEntity.currentPanel}`);
        }

        setEstimates(fetchedEstimates);
      } catch (err: any) {
        console.error("Failed to fetch related estimates:", err);
        setError(err.message || "Failed to load estimates");
      } finally {
        setLoading(false);
      }
    };

    fetchEstimates();
  }, [currentEntity?.currentId, currentEntity?.currentPanel]);

  // eslint-disable-next-line react-hooks/exhaustive-deps
  const getStatusColor = (status?: string) => {
    const colorKey = STATUS_COLORS[status?.toUpperCase() || "DRAFT"] || "todo";
    return colors[colorKey].border;
  };

  // eslint-disable-next-line react-hooks/exhaustive-deps
  const formatCurrency = (amount?: string | number | null) => formatCurrencyLib(amount, { blank: "—" });

  // Status options for editable picker
  const statusOptions: EditableOption[] = useMemo(
    () => [
      { value: "DRAFT", label: "Draft", color: colors.todo.border },
      { value: "SENT", label: "Sent", color: colors.pending.border },
      { value: "ACCEPTED", label: "Accepted", color: colors.complete.border },
      { value: "REJECTED", label: "Rejected", color: colors.attention.border },
      { value: "EXPIRED", label: "Expired", color: colors.neutral.border },
    ],
    [colors],
  );

  // Handle edit callback
  const handleEdit = useCallback(async (row: EstimateType, key: string, newValue: string) => {
    if (!row.id) return;

    try {
      await estimateService.updateEstimate(row.id, { [key]: newValue });
      setEstimates((prev) => prev.map((est) => (est.id === row.id ? { ...est, [key]: newValue } : est)));
    } catch (err) {
      console.error("Failed to update estimate:", err);
    }
  }, []);

  // Handle delete callback
  const handleDelete = useCallback(async (row: EstimateType) => {
    if (!row.id) return;

    try {
      await estimateService.deleteEstimatesByIds([row.id]);
      setEstimates((prev) => prev.filter((est) => est.id !== row.id));
    } catch (err) {
      console.error("Failed to delete estimate:", err);
    }
  }, []);

  const handleEstimatePress = useCallback(
    (estimate: EstimateType) => {
      // Save current entity to navigation history before navigating away
      if (currentEntity?.currentPanel && currentEntity?.currentId) {
        pushNavigation({
          panel: currentEntity.currentPanel,
          id: currentEntity.currentId,
          title: currentEntity.currentTitle || currentEntity.currentPanel,
        });
      }

      // Navigate to the estimate
      router.setParams({ panel: "estimate", id: estimate.id, tab: "overview", related: "" });
    },
    [currentEntity, pushNavigation],
  );

  const handleProjectPress = useCallback(
    (estimate: EstimateType) => {
      if (!estimate.project?.id) return;
      if (currentEntity?.currentPanel && currentEntity?.currentId) {
        pushNavigation({
          panel: currentEntity.currentPanel,
          id: currentEntity.currentId,
          title: currentEntity.currentTitle || currentEntity.currentPanel,
        });
      }
      router.setParams({ panel: "project", id: estimate.project.id, tab: "overview", related: "" });
    },
    [currentEntity, pushNavigation],
  );

  // Define columns for the table
  const columns: ColumnDef<EstimateType>[] = useMemo(() => {
    const cols: ColumnDef<EstimateType>[] = [];

    // Estimate number column - clickable for navigation
    cols.push({
      key: "estimate_number",
      header: "Number",
      flex: 1,
      minWidth: 100,
      accessor: (row: any) => row.meta_data?.estimate_number || row.estimate_number || "—",
      onPress: (row) => handleEstimatePress(row),
    });

    // Status column with editable picker
    cols.push({
      key: "status",
      header: "Status",
      width: 140,
      align: "center",
      searchable: true,
      searchValue: (_, row) => row.status || "DRAFT",
      editable: {
        type: "picker",
        options: statusOptions,
        placeholder: "Select status",
      },
      render: (value, row) => (
        <View style={[styles.statusPill, { backgroundColor: getStatusColor(row.status) }]}>
          <ThemedText style={styles.statusText}>{row.status?.toUpperCase() || "DRAFT"}</ThemedText>
        </View>
      ),
    });

    // Date column
    cols.push({
      key: "estimate_date",
      header: "Date",
      width: 100,
      searchable: false,
      accessor: (row: any) => row.meta_data?.estimate_date || row.estimate_date,
      render: (value) => <ThemedText style={styles.cellText}>{value ? formatDate(value) : "—"}</ThemedText>,
    });

    // Expiry column
    cols.push({
      key: "expiry_date",
      header: "Expires",
      width: 100,
      searchable: false,
      accessor: (row: any) => row.meta_data?.expiry_date || row.expiry_date,
      render: (value) => <ThemedText style={styles.cellText}>{value ? formatDate(value) : "—"}</ThemedText>,
    });

    // Amount column
    cols.push({
      key: "amount_due",
      header: "Amount",
      width: 140,
      align: "right",
      searchable: false,
      accessor: (row: any) => row.meta_data?.amount_due || row.amount_due,
      render: (value) => (
        <ThemedText style={styles.cellText}>{formatCurrency(value as string | number | null)}</ThemedText>
      ),
    });

    // Line items count column
    cols.push({
      key: "line_items",
      header: "Items",
      width: 80,
      align: "center",
      searchable: false,
      accessor: (row: any) => {
        const items = row.meta_data?.line_items || row.line_items;
        return Array.isArray(items) ? items.length : 0;
      },
      render: (value) => <ThemedText style={styles.cellText}>{value || 0}</ThemedText>,
    });

    // Project column (skip when viewing from project panel - redundant)
    if (currentEntity?.currentPanel !== "project") {
      cols.push({
        key: "project",
        header: displayNames?.projects?.name || "Project",
        flex: 1,
        minWidth: 120,
        accessor: (row: any) => row.project?.name || "—",
        onPress: (row) => handleProjectPress(row),
      });
    }

    return cols;
  }, [
    statusOptions,
    formatDate,
    formatCurrency,
    getStatusColor,
    handleEstimatePress,
    handleProjectPress,
    currentEntity?.currentPanel,
    displayNames?.projects?.name,
  ]);

  const estimatesName = displayNames?.estimates?.plural?.toLowerCase() || "estimates";

  if (error) {
    return (
      <View style={[styles.container, { backgroundColor: colors.background }]}>
        <View style={styles.emptyState}>
          <MaterialCommunityIcons name="alert-circle" size={48} color={colors.red.text} />
          <ThemedText style={styles.emptyText}>Failed to load {estimatesName}</ThemedText>
          <ThemedText style={{ opacity: 0.6, fontSize: 12 }}>{error}</ThemedText>
        </View>
      </View>
    );
  }

  return (
    <ScrollView
      horizontal={true}
      showsHorizontalScrollIndicator={true}
      contentContainerStyle={{ width: "auto", flexGrow: 1 }}
      style={[styles.container, { backgroundColor: colors.background }]}
    >
      <Table<EstimateType>
        data={estimates}
        columns={columns}
        keyExtractor={(item) => item.id || ""}
        loading={loading}
        loadingRowCount={8}
        onEdit={handleEdit}
        canEdit={() => estimatePermissions.update}
        onDelete={handleDelete}
        canDelete={() => estimatePermissions.delete}
        searchValue={searchValue}
        sortable
        defaultSort={{ key: "estimate_date", direction: "desc" }}
        emptyMessage={`No ${estimatesName} found`}
        emptyIcon="file-document-outline"
      />
    </ScrollView>
  );
}

const styles = StyleSheet.create({
  container: {
    flex: 1,
  },
  headerTitleContainer: {
    flexDirection: "row",
    alignItems: "center",
    gap: 12,
  },
  headerTitle: {
    fontSize: 17,
    fontWeight: "600",
  },
  headerSearchContainer: {
    flexDirection: "row",
    alignItems: "center",
    borderRadius: 8,
    paddingHorizontal: 10,
    paddingVertical: 6,
    minWidth: 140,
  },
  headerSearchIcon: {
    marginRight: 6,
  },
  headerSearchInput: {
    flex: 1,
    fontSize: 14,
    minWidth: 100,
    ...Platform.select({
      web: { outlineStyle: "none" as any },
    }),
  },
  headerClearButton: {
    padding: 2,
    marginLeft: 4,
  },
  statusPill: {
    paddingHorizontal: 8,
    paddingVertical: 2,
    borderRadius: 10,
  },
  statusText: {
    fontSize: 10,
    fontWeight: "600",
    color: "white",
  },
  cellText: {
    fontSize: 12,
  },
  emptyState: {
    flex: 1,
    justifyContent: "center",
    alignItems: "center",
    padding: 32,
    gap: 8,
  },
  emptyText: {
    fontSize: 16,
    fontWeight: "600",
    opacity: 0.6,
  },
});
